// ── Framework ──────────────────────────────────────────────────────────────
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// ── Layout & shared components ──────────────────────────────────────────────
import { Toaster } from '@/components/ui/sonner';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';

// ── Public pages ─────────────────────────────────────────────────────────────
import { Landing } from '@/pages/Landing';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { PetImageUpload } from '@/pages/PetImageUpload';

// ── Dashboard pages ──────────────────────────────────────────────────────────
import { Dashboard } from '@/pages/Dashboard';
import { Owners } from '@/pages/Owners';
import { OwnerView } from '@/pages/OwnerView';
import { Pets } from '@/pages/Pets';
import { PetView } from '@/pages/PetView';
import { PetTypes } from '@/pages/PetTypes';
import { Doctors } from '@/pages/Doctors';
import { Diagnoses } from '@/pages/Diagnoses';
import { Schedules } from '@/pages/Schedules';
import { Visits } from '@/pages/Visits';
import { VisitRecord } from '@/pages/VisitRecord';
import { Services } from '@/pages/Services';
import { VisitTypes } from '@/pages/VisitTypes';
import { Subscription } from '@/pages/Subscription';
import { Referral } from '@/pages/Referral';
import { Clinic } from '@/pages/Clinic';
import { ContactUs } from '@/pages/ContactUs';
import { Notifications } from '@/pages/Notifications';

// ── Medical record pages ─────────────────────────────────────────────────────
import { VaccinationRecords } from '@/pages/VaccinationRecords';
import { TreatmentRecords } from '@/pages/TreatmentRecords';
import { DiagnosisRecords } from '@/pages/DiagnosisRecords';
import { PrescriptionRecords } from '@/pages/PrescriptionRecords';
import { VisitServiceRecords } from '@/pages/VisitServiceRecords';

import '@/App.css';

// Handles subdomain-based routing between vetclinicapp.com and dashboard.vetclinicapp.com
function SubdomainGuard() {
  const location = useLocation();
  const hostname = window.location.hostname;
  const isDashboardSubdomain = hostname === 'app.vetclinicapp.com';
  const isMainDomain = hostname === 'vetclinicapp.com' || hostname === 'www.vetclinicapp.com';

  // On app.vetclinicapp.com, redirect bare root to /dashboard
  if (isDashboardSubdomain && location.pathname === '/') {
    return <Navigate to="/dashboard" replace />;
  }

  // On vetclinicapp.com, redirect everything except the landing page to the app subdomain
  // (login/register/dashboard must all live on app.vetclinicapp.com so localStorage is shared)
  if (isMainDomain && location.pathname !== '/') {
    window.location.replace(
      `https://app.vetclinicapp.com${location.pathname}${location.search}${location.hash}`
    );
    return null;
  }

  return null;
}

function App() {
  useTheme(); // applies the persisted theme on first render
  return (
    <>
      <BrowserRouter>
        <SubdomainGuard />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="owners" element={<Owners />} />
            <Route path="owners/:id" element={<OwnerView />} />
            <Route path="pets" element={<Pets />} />
            <Route path="pets/:id" element={<PetView />} />
            <Route path="pet-types" element={<PetTypes />} />
            <Route path="doctors" element={<Doctors />} />
            <Route path="diagnoses" element={<Diagnoses />} />
            <Route path="vaccinations" element={<VaccinationRecords />} />
            <Route path="treatments" element={<TreatmentRecords />} />
            <Route path="visit-diagnoses" element={<DiagnosisRecords />} />
            <Route path="prescriptions" element={<PrescriptionRecords />} />
            <Route path="visit-service-records" element={<VisitServiceRecords />} />
            <Route path="schedules" element={<Schedules />} />
            <Route path="visits" element={<Visits />} />
            <Route path="visits/:visitId" element={<VisitRecord />} />
            <Route path="services" element={<Services />} />
            <Route path="visit-types" element={<VisitTypes />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="referral" element={<Referral />} />
            <Route path="clinic" element={<Clinic />} />
            <Route path="contact" element={<ContactUs />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          <Route path="/pet-upload/:token" element={<PetImageUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
